/**
 * FormsTable Component
 * Responsive form list with search, quantity steppers for orderable forms,
 * and view/download actions for non-orderable (view-only) forms.
 *
 * Mobile: Card layout with stacked fields
 * Desktop: Table layout with columns
 */

import { useState, useMemo, useCallback, memo } from 'react';
import {
  EyeIcon,
  ArrowDownTrayIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  MinusIcon,
} from '@heroicons/react/24/outline';
import { OathEmptyState } from '../../shared/OathUI';

const QuantityStepper = memo(({ value, onChange }) => {
  const qty = value || 0;
  const decrement = () => { if (qty > 0) onChange(qty - 1); };
  const increment = () => { onChange(qty + 1); };
  const handleInput = (e) => {
    const val = parseInt(e.target.value, 10);
    onChange(isNaN(val) || val < 0 ? 0 : val);
  };
  return (
    <div className="inline-flex items-center border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden">
      <button type="button" onClick={decrement} disabled={qty <= 0}
        className="px-2.5 py-2 sm:px-2 sm:py-1.5 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        aria-label="Decrease quantity">
        <MinusIcon className="h-4 w-4 sm:h-3.5 sm:w-3.5" />
      </button>
      <input type="number" min="0" value={qty} onChange={handleInput}
        className="w-12 text-center text-sm border-x border-gray-300 dark:border-gray-600 py-1.5 bg-white dark:bg-gray-800 dark:text-white focus:outline-none focus:ring-1 focus:ring-primary [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
        aria-label="Quantity" />
      <button type="button" onClick={increment}
        className="px-2.5 py-2 sm:px-2 sm:py-1.5 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        aria-label="Increase quantity">
        <PlusIcon className="h-4 w-4 sm:h-3.5 sm:w-3.5" />
      </button>
    </div>
  );
});
QuantityStepper.displayName = 'QuantityStepper';

/**
 * FormsTable - Displays a list of forms with search, quantity input, and actions
 *
 * @param {Array} forms - Array of form objects
 * @param {Function} onView - (form) callback to open PDF viewer
 * @param {Function} onDownload - (form) callback to download form
 * @param {boolean} loading - Whether data is loading
 * @param {boolean} showOrderColumns - Whether to show Unit Size and Qty columns (for orderable/supply forms)
 */
const FormsTable = ({
  forms = [],
  cart = new Map(),
  onQuantityChange,
  onView,
  onDownload,
  loading = false,
  showOrderColumns = false,
  showActions = true,
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  const filteredForms = useMemo(() => {
    if (!searchTerm.trim()) return forms;
    const term = searchTerm.toLowerCase();
    return forms.filter((f) =>
      f.formName?.toLowerCase().includes(term) ||
      f.state?.toLowerCase().includes(term) ||
      f.fileName?.toLowerCase().includes(term)
    );
  }, [forms, searchTerm]);

  const handleQuantityChange = useCallback((formId, qty) => {
    onQuantityChange(formId, qty);
  }, [onQuantityChange]);

  // Loading skeleton
  if (loading) {
    return (
      <div className="space-y-3 p-4">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="animate-pulse flex items-center gap-4 p-3 rounded-lg bg-gray-50 dark:bg-gray-700/30">
            <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-1/3" />
            <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-16" />
            <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-12" />
            <div className="flex-1" />
            <div className="h-8 bg-gray-200 dark:bg-gray-600 rounded w-20" />
          </div>
        ))}
      </div>
    );
  }

  if (forms.length === 0) {
    return (
      <OathEmptyState
        title="No forms available"
        message="There are no forms matching your state and company."
      />
    );
  }

  return (
    <div>
      {/* Search bar */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="relative max-w-sm">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search forms..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-primary focus:border-primary transition-colors placeholder:text-gray-400 dark:placeholder:text-gray-500"
          />
        </div>
        {searchTerm && (
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            {filteredForms.length} of {forms.length} forms
          </p>
        )}
      </div>

      {filteredForms.length === 0 ? (
        <OathEmptyState
          title="No matches"
          message={`No forms match "${searchTerm}"`}
        />
      ) : (
        <>
          {/* Desktop table (hidden on mobile) */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-700/30">
                  <th className="text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide px-4 py-3">
                    Form
                  </th>
                  <th className="text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide px-4 py-3">
                    Description
                  </th>
                  <th className="text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide px-4 py-3 w-20">
                    State
                  </th>
                  {showOrderColumns && (
                    <th className="text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide px-4 py-3 w-36">
                      Qty
                    </th>
                  )}
                  {showActions && (
                    <th className="text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide px-4 py-3 w-28">
                      Actions
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
                {filteredForms.map((form) => (
                  <tr
                    key={form.formId}
                    className="hover:bg-gray-50/50 dark:hover:bg-gray-700/20 transition-colors"
                  >
                    <td className="px-4 py-3">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {form.formName}
                      </p>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-600 dark:text-gray-300">
                        {form.description || '-'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs font-medium text-gray-600 dark:text-gray-300">
                        {form.state || 'ALL'}
                      </span>
                    </td>
                    {showOrderColumns && (
                      <td className="px-4 py-3 text-center">
                        {form.orderable ? (
                          <QuantityStepper
                            value={cart.get(form.formId) || 0}
                            onChange={(qty) => handleQuantityChange(form.formId, qty)}
                          />
                        ) : (
                          <span className="text-xs text-gray-400 dark:text-gray-500 italic">
                            View only
                          </span>
                        )}
                      </td>
                    )}
                    {showActions && (
                      <td className="px-4 py-3">
                        {(!showOrderColumns || !form.orderable) && (
                          <div className="flex items-center justify-end gap-1">
                            <button
                              type="button"
                              onClick={() => onView(form)}
                              className="p-2 text-primary hover:bg-primary-50 dark:hover:bg-primary-900/30 rounded-lg transition-colors"
                              title="View form"
                            >
                              <EyeIcon className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => onDownload(form)}
                              className="p-2 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                              title="Download form"
                            >
                              <ArrowDownTrayIcon className="h-4 w-4" />
                            </button>
                          </div>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards (hidden on desktop) */}
          <div className="md:hidden divide-y divide-gray-100 dark:divide-gray-700/50">
            {filteredForms.map((form) => (
              <div key={form.formId} className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {form.formName}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {form.state || 'ALL'}
                      </span>
                    </div>
                    {form.description && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {form.description}
                      </p>
                    )}
                  </div>
                  {showActions && (!showOrderColumns || !form.orderable) && (
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        type="button"
                        onClick={() => onView(form)}
                        className="p-2 text-primary hover:bg-primary-50 dark:hover:bg-primary-900/30 rounded-lg transition-colors"
                        aria-label={`View ${form.formName}`}
                      >
                        <EyeIcon className="h-4.5 w-4.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => onDownload(form)}
                        className="p-2 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                        aria-label={`Download ${form.formName}`}
                      >
                        <ArrowDownTrayIcon className="h-4.5 w-4.5" />
                      </button>
                    </div>
                  )}
                </div>
                {showOrderColumns && form.orderable && (
                  <div className="mt-3 flex items-center justify-between">
                    <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Order Qty
                    </span>
                    <QuantityStepper
                      value={cart.get(form.formId) || 0}
                      onChange={(qty) => handleQuantityChange(form.formId, qty)}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default memo(FormsTable);
